import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { PROJECT_FILTERS, STATUS_FILTERS, DEPT_LIST } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Plus, X, Trash2 } from "lucide-react";

const EMPTY = {
  name: "", location: "", year: new Date().getFullYear(), status: "In Progress",
  services: [], project_type: "", description: "", cover_image_url: "",
  gallery: [], videos: [], video_links: [], before_images: [], after_images: [],
  facts: {}, is_published: false, is_featured: false, departments: [],
};

export default function PortalProjectEditor() {
  const { id } = useParams();
  const isNew = !id;
  const nav = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [factKey, setFactKey] = useState("");
  const [factVal, setFactVal] = useState("");
  const [galleryInput, setGalleryInput] = useState("");
  const [videoLinkInput, setVideoLinkInput] = useState("");

  useEffect(() => {
    if (isNew) return;
    api.get(`/projects/${id}`).then((r) => setForm({ ...EMPTY, ...r.data })).catch((e) => {
      toast.error("Could not load project");
      nav("/portal/projects");
    });
  }, [id, isNew, nav]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleService = (s) => {
    set("services", form.services.includes(s) ? form.services.filter((x) => x !== s) : [...form.services, s]);
  };
  const toggleDept = (d) => {
    set("departments", form.departments.includes(d) ? form.departments.filter((x) => x !== d) : [...form.departments, d]);
  };

  const addFact = () => {
    if (!factKey.trim()) return;
    set("facts", { ...form.facts, [factKey.trim()]: factVal.trim() });
    setFactKey(""); setFactVal("");
  };
  const removeFact = (k) => {
    const f = { ...form.facts }; delete f[k]; set("facts", f);
  };

  const addGalleryUrl = () => {
    if (!galleryInput.trim()) return;
    set("gallery", [...form.gallery, galleryInput.trim()]);
    setGalleryInput("");
  };
  const removeGalleryAt = (i) => set("gallery", form.gallery.filter((_, idx) => idx !== i));

  const addVideoLink = () => {
    if (!videoLinkInput.trim()) return;
    set("video_links", [...form.video_links, videoLinkInput.trim()]);
    setVideoLinkInput("");
  };

  const uploadMedia = async (file, kind) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    const { data } = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    // Serve via public /api/media/{path}
    return `/api/media/${data.storage_path}`;
  };

  const onUploadCover = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try { const url = await uploadMedia(file, "project"); set("cover_image_url", url); toast.success("Cover uploaded"); }
    catch { toast.error("Upload failed"); }
  };

  const onUploadGallery = async (e) => {
    const files = Array.from(e.target.files || []);
    const urls = [];
    for (const f of files) {
      try { urls.push(await uploadMedia(f, "project")); } catch { /* skip */ }
    }
    if (urls.length) { set("gallery", [...form.gallery, ...urls]); toast.success(`Uploaded ${urls.length} image(s)`); }
  };

  const onUploadVideos = async (e) => {
    const files = Array.from(e.target.files || []);
    const urls = [];
    for (const f of files) {
      try { urls.push(await uploadMedia(f, "project_video")); } catch { /* skip */ }
    }
    if (urls.length) { set("videos", [...(form.videos || []), ...urls]); toast.success(`Uploaded ${urls.length} video(s)`); }
  };

  const onUploadBeforeAfter = async (e, key) => {
    const files = Array.from(e.target.files || []);
    const urls = [];
    for (const f of files) {
      try { urls.push(await uploadMedia(f, "project")); } catch { /* skip */ }
    }
    if (urls.length) { set(key, [...(form[key] || []), ...urls]); toast.success(`Uploaded ${urls.length} image(s)`); }
  };

  const save = async (publish = null) => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (publish !== null) payload.is_published = publish;
      if (isNew) {
        const { data } = await api.post("/projects", payload);
        toast.success("Project created");
        nav(`/portal/projects/${data.id}`, { replace: true });
      } else {
        await api.patch(`/projects/${id}`, payload);
        toast.success(publish === true ? "Published" : publish === false ? "Unpublished" : "Saved");
        if (publish !== null) setForm((f) => ({ ...f, is_published: publish }));
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    if (!window.confirm("Delete this project? This cannot be undone.")) return;
    try { await api.delete(`/projects/${id}`); toast.success("Deleted"); nav("/portal/projects"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-6 lg:p-12 max-w-[1200px]" data-testid="portal-project-editor">
      <Link to="/portal/projects" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-stone hover:text-ink mb-6">
        <ArrowLeft size={13} /> Back to projects
      </Link>

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
        <div>
          <div className="eyebrow mb-1">{isNew ? "New project" : "Edit project"}</div>
          <h1 className="font-heading font-light text-3xl lg:text-4xl tracking-tighter">
            {isNew ? "Create project" : form.name || "Untitled"}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {!isNew && (
            <Button variant="outline" onClick={doDelete} data-testid="project-delete" className="rounded-none border-line text-destructive hover:bg-destructive hover:text-white">
              <Trash2 size={14} className="mr-2" /> Delete
            </Button>
          )}
          <Button variant="outline" onClick={() => save(false)} data-testid="project-save-draft" disabled={saving} className="rounded-none border-line">
            Save draft
          </Button>
          <Button onClick={() => save(true)} data-testid="project-publish" disabled={saving} className="bg-ink text-white hover:bg-ink/90 rounded-none">
            {form.is_published ? "Save & publish" : "Publish"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-8">
        <div className="col-span-12 lg:col-span-8 space-y-8">
          <Field label="Name">
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="pf-name" />
          </Field>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Location">
              <Input value={form.location} onChange={(e) => set("location", e.target.value)} data-testid="pf-location" />
            </Field>
            <Field label="Year">
              <Input type="number" value={form.year || ""} onChange={(e) => set("year", parseInt(e.target.value) || null)} data-testid="pf-year" />
            </Field>
            <Field label="Status">
              <select className="line-input h-10" value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="pf-status">
                <option>In Progress</option>
                <option>Completed</option>
                <option>On Hold</option>
                <option>Draft</option>
              </select>
            </Field>
          </div>

          <Field label="Project type">
            <div className="flex flex-wrap gap-2">
              {["Residential", "Commercial", "Interior Design"].map((t) => (
                <button
                  key={t} type="button"
                  onClick={() => set("project_type", t)}
                  className={`text-xs uppercase tracking-widest px-3 py-2 border ${form.project_type === t ? "bg-ink text-white border-ink" : "border-line hover:border-ink"}`}
                >{t}</button>
              ))}
            </div>
          </Field>

          <Field label="Services">
            <div className="flex flex-wrap gap-2">
              {PROJECT_FILTERS.filter((f) => f !== "All").map((s) => (
                <button
                  key={s} type="button"
                  onClick={() => toggleService(s)}
                  className={`text-xs uppercase tracking-widest px-3 py-2 border ${form.services.includes(s) ? "bg-ink text-white border-ink" : "border-line hover:border-ink"}`}
                >{s}</button>
              ))}
            </div>
          </Field>

          <Field label="Departments">
            <div className="flex flex-wrap gap-2">
              {DEPT_LIST.map((d) => (
                <button
                  key={d.key} type="button"
                  onClick={() => toggleDept(d.key)}
                  className={`text-xs uppercase tracking-widest px-3 py-2 border ${form.departments.includes(d.key) ? "bg-ink text-white border-ink" : "border-line hover:border-ink"}`}
                >{d.label}</button>
              ))}
            </div>
          </Field>

          <Field label="Description">
            <Textarea rows={5} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="pf-description" />
          </Field>

          <Field label="Cover image">
            {form.cover_image_url && (
              <img src={mediaUrl(form.cover_image_url)} alt="cover" className="w-full max-w-md aspect-[4/3] object-cover border border-line mb-3" />
            )}
            <div className="flex flex-col gap-2">
              <Input value={form.cover_image_url} placeholder="Paste image URL…" onChange={(e) => set("cover_image_url", e.target.value)} data-testid="pf-cover-url" />
              <label className="text-xs cursor-pointer text-ink border border-line px-3 py-2 w-fit hover:border-ink" data-testid="pf-cover-upload">
                or upload from computer
                <input type="file" accept="image/*" hidden onChange={onUploadCover} />
              </label>
            </div>
          </Field>

          <Field label="Gallery images">
            <div className="grid grid-cols-3 md:grid-cols-5 gap-2 mb-3">
              {form.gallery.map((g, i) => (
                <div key={i} className="relative aspect-square bg-sand border border-line group">
                  <img src={mediaUrl(g)} alt={`gallery ${i}`} className="w-full h-full object-cover" />
                  <button onClick={() => removeGalleryAt(i)} className="absolute top-1 right-1 bg-white/95 p-1"><X size={12} /></button>
                </div>
              ))}
            </div>
            <div className="flex flex-col md:flex-row gap-2">
              <div className="flex-1 flex gap-2">
                <Input value={galleryInput} onChange={(e) => setGalleryInput(e.target.value)} placeholder="Image URL…" />
                <Button variant="outline" onClick={addGalleryUrl} className="rounded-none border-line">Add</Button>
              </div>
              <label className="text-xs cursor-pointer text-ink border border-line px-3 py-2 hover:border-ink inline-flex items-center gap-2">
                <Plus size={12} /> Upload
                <input type="file" accept="image/*" multiple hidden onChange={onUploadGallery} />
              </label>
            </div>
          </Field>

          <Field label="Video links">
            <div className="space-y-2 mb-3">
              {form.video_links.map((v, i) => (
                <div key={i} className="flex items-center justify-between border border-line px-3 py-2 text-sm font-mono">
                  <span className="truncate">{v}</span>
                  <button onClick={() => set("video_links", form.video_links.filter((_, idx) => idx !== i))}><X size={12} /></button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Input value={videoLinkInput} onChange={(e) => setVideoLinkInput(e.target.value)} placeholder="https://vimeo.com/… or youtube URL" />
              <Button variant="outline" onClick={addVideoLink} className="rounded-none border-line">Add</Button>
            </div>
          </Field>

          <Field label="Uploaded videos (MP4 / WebM)">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
              {(form.videos || []).map((v, i) => (
                <div key={i} className="relative border border-line bg-black aspect-video group">
                  <video src={mediaUrl(v)} muted loop playsInline preload="metadata" className="w-full h-full object-cover" />
                  <button onClick={() => set("videos", form.videos.filter((_, idx) => idx !== i))} className="absolute top-1 right-1 bg-white/95 p-1" data-testid={`remove-video-${i}`}><X size={12} /></button>
                </div>
              ))}
            </div>
            <label className="cursor-pointer inline-flex items-center gap-2 border border-line px-3 py-2 text-xs hover:border-ink" data-testid="upload-project-video">
              <Plus size={12} /> Upload videos
              <input type="file" accept="video/mp4,video/webm,.mp4,.webm" multiple hidden onChange={onUploadVideos} />
            </label>
          </Field>

          <Field label="Before &amp; After images">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <BeforeAfterColumn
                label="Before"
                items={form.before_images || []}
                onRemove={(i) => set("before_images", form.before_images.filter((_, idx) => idx !== i))}
                onUpload={(e) => onUploadBeforeAfter(e, "before_images")}
                testId="before"
              />
              <BeforeAfterColumn
                label="After"
                items={form.after_images || []}
                onRemove={(i) => set("after_images", form.after_images.filter((_, idx) => idx !== i))}
                onUpload={(e) => onUploadBeforeAfter(e, "after_images")}
                testId="after"
              />
            </div>
          </Field>
        </div>

        <aside className="col-span-12 lg:col-span-4 space-y-8">
          <div className="border border-line bg-white p-5 space-y-4">
            <div className="eyebrow">Visibility</div>
            <div className="flex items-center justify-between">
              <Label htmlFor="published">Published</Label>
              <Switch id="published" checked={form.is_published} onCheckedChange={(v) => set("is_published", v)} data-testid="pf-published" />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="featured">Feature on homepage</Label>
              <Switch id="featured" checked={form.is_featured} onCheckedChange={(v) => set("is_featured", v)} data-testid="pf-featured" />
            </div>
          </div>

          <div className="border border-line bg-white p-5">
            <div className="eyebrow mb-3">Project facts</div>
            <div className="space-y-2 mb-4">
              {Object.entries(form.facts).map(([k, v]) => (
                <div key={k} className="flex justify-between items-center text-sm border-b border-line py-1">
                  <div><span className="text-stone">{k}:</span> {v}</div>
                  <button onClick={() => removeFact(k)}><X size={12} /></button>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input value={factKey} onChange={(e) => setFactKey(e.target.value)} placeholder="Key" />
              <Input value={factVal} onChange={(e) => setFactVal(e.target.value)} placeholder="Value" />
            </div>
            <Button variant="outline" onClick={addFact} className="mt-2 rounded-none border-line w-full">Add fact</Button>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="eyebrow mb-2">{label}</div>
      {children}
    </div>
  );
}

function BeforeAfterColumn({ label, items, onRemove, onUpload, testId }) {
  return (
    <div>
      <div className="eyebrow text-stone mb-2">{label}</div>
      <div className="grid grid-cols-2 gap-2 mb-3">
        {items.map((src, i) => (
          <div key={i} className="relative aspect-[4/3] bg-sand border border-line">
            <img src={mediaUrl(src)} alt={`${label} ${i}`} className="w-full h-full object-cover" />
            <button onClick={() => onRemove(i)} className="absolute top-1 right-1 bg-white/95 p-1" data-testid={`remove-${testId}-${i}`}><X size={12} /></button>
          </div>
        ))}
      </div>
      <label className="cursor-pointer inline-flex items-center gap-2 border border-line px-3 py-2 text-xs hover:border-ink" data-testid={`upload-${testId}`}>
        <Plus size={12} /> Add {label} image
        <input type="file" accept="image/*" multiple hidden onChange={onUpload} />
      </label>
    </div>
  );
}
